import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import './App.css';

import Hero from './components/Hero';
import MenuSection from './components/MenuSection';
import Footer from './components/Footer';
import LegalDocumentModal from './components/LegalDocumentModal';
import CookieConsent from './components/CookieConsent';

import { getSiteSettings } from './api/pizzaApi';

const LEGAL_HASH_TO_DOCUMENT_TYPE = {
  '#privacy': 'privacy',
  '#terms': 'terms',
  '#cookie-policy': 'cookie_policy',
};

const DEFAULT_RETURN_HASH = '#home';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const SITE_LANGUAGE_PATTERN = /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/;

const DEFAULT_SITE_TITLE = 'Pizzeria';
const DEFAULT_SITE_LANGUAGE = 'it';
const DEFAULT_ROBOTS_CONTENT = 'index, follow';

const STRUCTURED_DATA_SCRIPT_ID = 'site-structured-data';

const SCHEMA_BUSINESS_TYPES = new Set([
  'LocalBusiness',
  'FoodEstablishment',
  'Restaurant',
  'FastFoodRestaurant',
  'CafeOrCoffeeShop',
  'Bakery',
  'BarOrPub',
]);

const AppPage = styled.main`
  width: 100%;
  min-height: 100vh;

  background: #f5f2ee;
`;

let siteSettingsRequest = null;

const loadSiteSettings = () => {
  if (!siteSettingsRequest) {
    siteSettingsRequest = getSiteSettings().catch((error) => {
      siteSettingsRequest = null;

      throw error;
    });
  }

  return siteSettingsRequest;
};

function getLegalDocumentTypeFromHash(hash) {
  if (typeof hash !== 'string') {
    return null;
  }

  return LEGAL_HASH_TO_DOCUMENT_TYPE[hash.toLowerCase()] || null;
}

function getCurrentHash() {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.hash;
}

function getCurrentLegalDocumentType() {
  return getLegalDocumentTypeFromHash(getCurrentHash());
}

function getInitialReturnHash() {
  const currentHash = getCurrentHash();

  if (!currentHash || getLegalDocumentTypeFromHash(currentHash)) {
    return DEFAULT_RETURN_HASH;
  }

  return currentHash;
}

function getLegalDocument(documents, documentType) {
  if (!Array.isArray(documents) || !documentType) {
    return null;
  }

  return (
    documents.find(
      (document) =>
        document && typeof document === 'object' && document.document_type === documentType,
    ) || null
  );
}

function getNonEmptyString(value, fallback = '') {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return normalizedValue || fallback;
}

function getSafeAssetURL(value) {
  const normalizedValue = getNonEmptyString(value);

  if (!normalizedValue || typeof window === 'undefined') {
    return null;
  }

  try {
    const assetURL = new URL(normalizedValue, window.location.origin);

    if (!['http:', 'https:'].includes(assetURL.protocol)) {
      return null;
    }

    return assetURL.href;
  } catch {
    return null;
  }
}

function getSafePageURL(value, { stripHash = false } = {}) {
  const normalizedValue = getNonEmptyString(value);

  if (!normalizedValue || typeof window === 'undefined') {
    return null;
  }

  try {
    const pageURL = new URL(normalizedValue, window.location.origin);

    if (!['http:', 'https:'].includes(pageURL.protocol)) {
      return null;
    }

    if (stripHash) {
      pageURL.hash = '';
    }

    return pageURL.href;
  } catch {
    return null;
  }
}

function isPrivateOrLocalHostname(hostname) {
  const normalizedHostname = getNonEmptyString(hostname).toLowerCase();

  if (
    !normalizedHostname ||
    normalizedHostname === 'localhost' ||
    normalizedHostname === '0.0.0.0' ||
    normalizedHostname === '::1' ||
    normalizedHostname === '[::1]' ||
    normalizedHostname.endsWith('.local')
  ) {
    return true;
  }

  if (/^127(?:\.\d{1,3}){3}$/.test(normalizedHostname)) {
    return true;
  }

  if (/^10(?:\.\d{1,3}){3}$/.test(normalizedHostname)) {
    return true;
  }

  if (/^192\.168(?:\.\d{1,3}){2}$/.test(normalizedHostname)) {
    return true;
  }

  const private172Match = normalizedHostname.match(/^172\.(\d{1,3})(?:\.\d{1,3}){2}$/);

  if (private172Match) {
    const secondOctet = Number(private172Match[1]);

    if (secondOctet >= 16 && secondOctet <= 31) {
      return true;
    }
  }

  return false;
}

function getReliableCurrentPageURL() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const pageURL = new URL(window.location.href);

    if (
      !['http:', 'https:'].includes(pageURL.protocol) ||
      isPrivateOrLocalHostname(pageURL.hostname)
    ) {
      return null;
    }

    pageURL.hash = '';
    pageURL.search = '';

    return pageURL.href;
  } catch {
    return null;
  }
}

function getPublicPageURL(value, { stripHash = false } = {}) {
  const pageURL = getSafePageURL(value, {
    stripHash,
  });

  if (!pageURL) {
    return null;
  }

  try {
    const parsedURL = new URL(pageURL);

    if (isPrivateOrLocalHostname(parsedURL.hostname)) {
      return null;
    }

    return parsedURL.href;
  } catch {
    return null;
  }
}

function getPublicExternalURL(value) {
  const normalizedValue = getNonEmptyString(value);

  if (!/^https?:\/\//i.test(normalizedValue)) {
    return null;
  }

  return getPublicPageURL(normalizedValue);
}

function getCommaSeparatedValues(value) {
  const normalizedValue = getNonEmptyString(value);

  if (!normalizedValue) {
    return [];
  }

  return normalizedValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function getOpeningHoursValues(value) {
  const normalizedValue = getNonEmptyString(value);

  if (!normalizedValue) {
    return [];
  }

  return normalizedValue
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getSocialProfileURLs(socialLinks) {
  if (!Array.isArray(socialLinks)) {
    return [];
  }

  return [
    ...new Set(
      socialLinks.map((socialLink) => getPublicExternalURL(socialLink?.url)).filter(Boolean),
    ),
  ];
}

function setStructuredData(value) {
  if (typeof document === 'undefined') {
    return;
  }

  const matchingElements = Array.from(
    document.head.querySelectorAll(`script#${STRUCTURED_DATA_SCRIPT_ID}`),
  );

  if (!value || typeof value !== 'object') {
    matchingElements.forEach((element) => {
      element.remove();
    });

    return;
  }

  const [primaryElement, ...duplicateElements] = matchingElements;

  const element = primaryElement || document.createElement('script');

  element.id = STRUCTURED_DATA_SCRIPT_ID;
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);

  if (!primaryElement) {
    document.head.appendChild(element);
  }

  duplicateElements.forEach((duplicateElement) => {
    duplicateElement.remove();
  });
}

function buildStructuredData(siteSettings) {
  const seoSettings = siteSettings?.seo;

  if (seoSettings?.structured_data_enabled !== true) {
    return null;
  }

  const businessName = getNonEmptyString(
    siteSettings?.top?.site_name,
    getNonEmptyString(seoSettings?.og_site_name),
  );

  if (!businessName) {
    return null;
  }

  const configuredBusinessType = getNonEmptyString(seoSettings?.schema_business_type);

  const businessType = SCHEMA_BUSINESS_TYPES.has(configuredBusinessType)
    ? configuredBusinessType
    : 'LocalBusiness';

  const canonicalURL =
    getPublicPageURL(seoSettings?.canonical_url, {
      stripHash: true,
    }) ||
    getPublicPageURL(seoSettings?.og_url, {
      stripHash: true,
    }) ||
    getReliableCurrentPageURL();

  const logoURL = getSafeAssetURL(siteSettings?.top?.logo);

  const imageURL =
    getSafeAssetURL(seoSettings?.og_image) ||
    getSafeAssetURL(siteSettings?.header?.background_image) ||
    logoURL;

  const description = getNonEmptyString(
    seoSettings?.meta_description,
    getNonEmptyString(siteSettings?.footer?.description),
  );

  const telephone = getNonEmptyString(
    siteSettings?.footer?.contact_phone,
    getNonEmptyString(siteSettings?.top?.phone_number),
  );

  const email = getNonEmptyString(siteSettings?.footer?.contact_email);

  const address = getNonEmptyString(siteSettings?.footer?.contact_address);

  const priceRange = getNonEmptyString(seoSettings?.schema_price_range);

  const servesCuisine = getCommaSeparatedValues(seoSettings?.schema_serves_cuisine);

  const openingHours = getOpeningHoursValues(seoSettings?.schema_opening_hours);

  const sameAs = getSocialProfileURLs(siteSettings?.footer?.social_links);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': businessType,
    name: businessName,
  };

  if (canonicalURL) {
    structuredData['@id'] = `${canonicalURL}#business`;
    structuredData.url = canonicalURL;
  }

  if (description) {
    structuredData.description = description;
  }

  if (logoURL) {
    structuredData.logo = logoURL;
  }

  if (imageURL) {
    structuredData.image = imageURL;
  }

  if (telephone) {
    structuredData.telephone = telephone;
  }

  if (email) {
    structuredData.email = email;
  }

  if (address) {
    structuredData.address = address;
  }

  if (priceRange) {
    structuredData.priceRange = priceRange;
  }

  if (servesCuisine.length > 0) {
    structuredData.servesCuisine = servesCuisine;
  }

  if (openingHours.length > 0) {
    structuredData.openingHours = openingHours;
  }

  if (sameAs.length > 0) {
    structuredData.sameAs = sameAs;
  }

  return structuredData;
}

function getValidSiteLanguage(value, fallback = DEFAULT_SITE_LANGUAGE) {
  const normalizedValue = getNonEmptyString(value);

  if (!SITE_LANGUAGE_PATTERN.test(normalizedValue)) {
    return fallback;
  }

  return normalizedValue;
}

function getMetadataSelector(attributeName, attributeValue) {
  return `meta[${attributeName}="${attributeValue}"]`;
}

function getMetaContent(attributeName, attributeValue) {
  if (typeof document === 'undefined') {
    return null;
  }

  const element = document.head.querySelector(getMetadataSelector(attributeName, attributeValue));

  return element?.getAttribute('content') ?? null;
}

function setMetaContent(attributeName, attributeValue, content) {
  if (typeof document === 'undefined') {
    return;
  }

  const normalizedContent = getNonEmptyString(content);

  const selector = getMetadataSelector(attributeName, attributeValue);

  const matchingElements = Array.from(document.head.querySelectorAll(selector));

  if (!normalizedContent) {
    matchingElements.forEach((element) => {
      element.remove();
    });

    return;
  }

  const [primaryElement, ...duplicateElements] = matchingElements;

  const element = primaryElement || document.createElement('meta');

  element.setAttribute(attributeName, attributeValue);
  element.setAttribute('content', normalizedContent);

  if (!primaryElement) {
    document.head.appendChild(element);
  }

  duplicateElements.forEach((duplicateElement) => {
    duplicateElement.remove();
  });
}

function getCanonicalHref() {
  if (typeof document === 'undefined') {
    return null;
  }

  const canonicalElement = document.head.querySelector('link[rel="canonical"]');

  return canonicalElement?.getAttribute('href') ?? null;
}

function setCanonicalHref(value) {
  if (typeof document === 'undefined') {
    return;
  }

  const normalizedValue = getNonEmptyString(value);

  const matchingElements = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));

  if (!normalizedValue) {
    matchingElements.forEach((element) => {
      element.remove();
    });

    return;
  }

  const [primaryElement, ...duplicateElements] = matchingElements;

  const element = primaryElement || document.createElement('link');

  element.setAttribute('rel', 'canonical');
  element.setAttribute('href', normalizedValue);

  if (!primaryElement) {
    document.head.appendChild(element);
  }

  duplicateElements.forEach((duplicateElement) => {
    duplicateElement.remove();
  });
}

function captureSeoMetadataFallback() {
  if (typeof document === 'undefined') {
    return null;
  }

  return {
    title: document.title || DEFAULT_SITE_TITLE,
    language: getValidSiteLanguage(document.documentElement.lang, DEFAULT_SITE_LANGUAGE),

    metaDescription: getMetaContent('name', 'description'),
    robots: getMetaContent('name', 'robots'),
    canonicalHref: getCanonicalHref(),

    ogTitle: getMetaContent('property', 'og:title'),
    ogDescription: getMetaContent('property', 'og:description'),
    ogImage: getMetaContent('property', 'og:image'),
    ogURL: getMetaContent('property', 'og:url'),
    ogSiteName: getMetaContent('property', 'og:site_name'),
    ogType: getMetaContent('property', 'og:type'),

    twitterCard: getMetaContent('name', 'twitter:card'),
    twitterTitle: getMetaContent('name', 'twitter:title'),
    twitterDescription: getMetaContent('name', 'twitter:description'),
    twitterImage: getMetaContent('name', 'twitter:image'),
  };
}

function restoreSeoMetadataFallback(fallbackValues) {
  if (!fallbackValues || typeof document === 'undefined') {
    return;
  }

  document.title = fallbackValues.title;
  document.documentElement.lang = fallbackValues.language;

  setMetaContent('name', 'description', fallbackValues.metaDescription);
  setMetaContent('name', 'robots', fallbackValues.robots);
  setCanonicalHref(fallbackValues.canonicalHref);

  setMetaContent('property', 'og:title', fallbackValues.ogTitle);
  setMetaContent('property', 'og:description', fallbackValues.ogDescription);
  setMetaContent('property', 'og:image', fallbackValues.ogImage);
  setMetaContent('property', 'og:url', fallbackValues.ogURL);
  setMetaContent('property', 'og:site_name', fallbackValues.ogSiteName);
  setMetaContent('property', 'og:type', fallbackValues.ogType);

  setMetaContent('name', 'twitter:card', fallbackValues.twitterCard);
  setMetaContent('name', 'twitter:title', fallbackValues.twitterTitle);
  setMetaContent('name', 'twitter:description', fallbackValues.twitterDescription);
  setMetaContent('name', 'twitter:image', fallbackValues.twitterImage);
}

function getValidHexColor(value, fallback) {
  const normalizedValue = getNonEmptyString(value);

  if (!HEX_COLOR_PATTERN.test(normalizedValue)) {
    return fallback;
  }

  return normalizedValue;
}

function buildDynamicManifest(siteIcons, fallbackValues) {
  const appName = getNonEmptyString(siteIcons?.app_name, 'Pizzeria');

  const shortName = getNonEmptyString(siteIcons?.short_name, appName);

  const themeColor = getValidHexColor(siteIcons?.theme_color, fallbackValues.themeColor);

  const backgroundColor = getValidHexColor(siteIcons?.background_color, '#ffffff');

  const pwaIcon192 = getSafeAssetURL(siteIcons?.pwa_icon_192);

  const pwaIcon512 = getSafeAssetURL(siteIcons?.pwa_icon_512);

  const icons = [];

  if (pwaIcon192) {
    icons.push({
      src: pwaIcon192,
      sizes: '192x192',
      type: 'image/png',
      purpose: 'any',
    });
  }

  if (pwaIcon512) {
    icons.push({
      src: pwaIcon512,
      sizes: '512x512',
      type: 'image/png',
      purpose: 'any',
    });
  }

  const appStartURL = new URL('/', window.location.origin).href;

  const manifest = {
    id: appStartURL,
    name: appName,
    short_name: shortName,
    start_url: appStartURL,
    scope: appStartURL,
    display: 'standalone',
    theme_color: themeColor,
    background_color: backgroundColor,
  };

  if (icons.length > 0) {
    manifest.icons = icons;
  }

  return manifest;
}

function App() {
  const [siteSettings, setSiteSettings] = useState(null);

  const [isSiteSettingsResolved, setIsSiteSettingsResolved] = useState(false);

  const [preferencesRequest, setPreferencesRequest] = useState(0);

  const [activeLegalDocumentType, setActiveLegalDocumentType] = useState(
    getCurrentLegalDocumentType,
  );

  const [returnFocusElement, setReturnFocusElement] = useState(null);

  const activeLegalDocumentTypeRef = useRef(activeLegalDocumentType);

  const lastNonLegalHashRef = useRef(getInitialReturnHash());

  const browserMetadataFallbackRef = useRef(null);

  const seoMetadataFallbackRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    loadSiteSettings()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setSiteSettings(data);
        setIsSiteSettingsResolved(true);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setIsSiteSettingsResolved(true);

        console.error(
          '[PizzaKebab] Не удалось загрузить настройки сайта. Используются fallback-данные.',
          error,
        );
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const siteIcons = siteSettings?.site_icons;

  const seoSettings = siteSettings?.seo;

  const siteTitle = getNonEmptyString(siteSettings?.top?.site_name, DEFAULT_SITE_TITLE);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (!seoMetadataFallbackRef.current) {
      seoMetadataFallbackRef.current = captureSeoMetadataFallback();
    }

    return () => {
      restoreSeoMetadataFallback(seoMetadataFallbackRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const fallbackValues = seoMetadataFallbackRef.current || captureSeoMetadataFallback();

    if (!fallbackValues) {
      return;
    }

    const browserTitle = getNonEmptyString(seoSettings?.browser_title, siteTitle);

    const metaDescription = getNonEmptyString(
      seoSettings?.meta_description,
      getNonEmptyString(fallbackValues.metaDescription),
    );

    const canonicalURL =
      getSafePageURL(seoSettings?.canonical_url, {
        stripHash: true,
      }) ||
      getSafePageURL(fallbackValues.canonicalHref, {
        stripHash: true,
      }) ||
      getReliableCurrentPageURL();

    const siteLanguage = getValidSiteLanguage(
      seoSettings?.site_language,
      fallbackValues.language || DEFAULT_SITE_LANGUAGE,
    );

    const robotsContent = [
      seoSettings?.robots_index === false ? 'noindex' : 'index',
      seoSettings?.robots_follow === false ? 'nofollow' : 'follow',
    ].join(', ');

    const ogTitle = getNonEmptyString(seoSettings?.og_title, browserTitle);

    const ogDescription = getNonEmptyString(seoSettings?.og_description, metaDescription);

    const ogImage =
      getSafeAssetURL(seoSettings?.og_image) ||
      getSafeAssetURL(siteSettings?.header?.background_image) ||
      getSafeAssetURL(siteSettings?.top?.logo) ||
      getSafeAssetURL(fallbackValues.ogImage);

    const ogURL =
      getSafePageURL(seoSettings?.og_url, {
        stripHash: true,
      }) ||
      canonicalURL ||
      getSafePageURL(fallbackValues.ogURL, {
        stripHash: true,
      });

    const ogSiteName = getNonEmptyString(seoSettings?.og_site_name, siteTitle);

    const ogType = getNonEmptyString(
      seoSettings?.og_type,
      getNonEmptyString(fallbackValues.ogType, 'website'),
    );

    const twitterCard = getNonEmptyString(
      seoSettings?.twitter_card,
      getNonEmptyString(fallbackValues.twitterCard, 'summary_large_image'),
    );

    const twitterTitle = getNonEmptyString(seoSettings?.twitter_title, ogTitle);

    const twitterDescription = getNonEmptyString(seoSettings?.twitter_description, ogDescription);

    const twitterImage = getSafeAssetURL(seoSettings?.twitter_image) || ogImage;

    document.title = browserTitle;
    document.documentElement.lang = siteLanguage;

    setMetaContent('name', 'description', metaDescription);
    setMetaContent(
      'name',
      'robots',
      robotsContent || fallbackValues.robots || DEFAULT_ROBOTS_CONTENT,
    );

    setCanonicalHref(canonicalURL);

    setMetaContent('property', 'og:title', ogTitle);
    setMetaContent('property', 'og:description', ogDescription);
    setMetaContent('property', 'og:image', ogImage);
    setMetaContent('property', 'og:url', ogURL);
    setMetaContent('property', 'og:site_name', ogSiteName);
    setMetaContent('property', 'og:type', ogType);

    setMetaContent('name', 'twitter:card', twitterCard);
    setMetaContent('name', 'twitter:title', twitterTitle);
    setMetaContent('name', 'twitter:description', twitterDescription);
    setMetaContent('name', 'twitter:image', twitterImage);
  }, [seoSettings, siteSettings, siteTitle]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    setStructuredData(buildStructuredData(siteSettings));

    return () => {
      setStructuredData(null);
    };
  }, [siteSettings]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const faviconElement = document.getElementById('site-favicon');

    const appleTouchIconElement = document.getElementById('site-apple-touch-icon');

    const themeColorElement = document.getElementById('site-theme-color');

    const manifestElement = document.getElementById('site-manifest');

    if (!browserMetadataFallbackRef.current) {
      browserMetadataFallbackRef.current = {
        faviconHref: faviconElement?.getAttribute('href') || '/favicon.ico',
        faviconType: faviconElement?.getAttribute('type'),
        faviconSizes: faviconElement?.getAttribute('sizes'),

        appleTouchIconHref: appleTouchIconElement?.getAttribute('href') || '/logo192.png',
        appleTouchIconSizes: appleTouchIconElement?.getAttribute('sizes'),

        themeColor: themeColorElement?.getAttribute('content') || '#000000',

        manifestHref: manifestElement?.getAttribute('href') || '/manifest.json',
      };
    }

    const fallbackValues = browserMetadataFallbackRef.current;

    const restoreFallbackMetadata = () => {
      if (faviconElement) {
        faviconElement.setAttribute('href', fallbackValues.faviconHref);

        if (fallbackValues.faviconType) {
          faviconElement.setAttribute('type', fallbackValues.faviconType);
        } else {
          faviconElement.removeAttribute('type');
        }

        if (fallbackValues.faviconSizes) {
          faviconElement.setAttribute('sizes', fallbackValues.faviconSizes);
        } else {
          faviconElement.removeAttribute('sizes');
        }
      }

      if (appleTouchIconElement) {
        appleTouchIconElement.setAttribute('href', fallbackValues.appleTouchIconHref);

        if (fallbackValues.appleTouchIconSizes) {
          appleTouchIconElement.setAttribute('sizes', fallbackValues.appleTouchIconSizes);
        } else {
          appleTouchIconElement.removeAttribute('sizes');
        }
      }

      if (themeColorElement) {
        themeColorElement.setAttribute('content', fallbackValues.themeColor);
      }

      if (manifestElement) {
        manifestElement.setAttribute('href', fallbackValues.manifestHref);
      }
    };

    restoreFallbackMetadata();

    if (!siteIcons || typeof siteIcons !== 'object') {
      return restoreFallbackMetadata;
    }

    const faviconURL = getSafeAssetURL(siteIcons.favicon);

    if (faviconElement && faviconURL) {
      faviconElement.setAttribute('href', faviconURL);
      faviconElement.setAttribute('type', 'image/png');
      faviconElement.setAttribute('sizes', '64x64');
    }

    const appleTouchIconURL = getSafeAssetURL(siteIcons.apple_touch_icon);

    if (appleTouchIconElement && appleTouchIconURL) {
      appleTouchIconElement.setAttribute('href', appleTouchIconURL);
      appleTouchIconElement.setAttribute('sizes', '180x180');
    }

    if (themeColorElement) {
      themeColorElement.setAttribute(
        'content',
        getValidHexColor(siteIcons.theme_color, fallbackValues.themeColor),
      );
    }

    let manifestObjectURL = null;

    if (manifestElement) {
      if (siteIcons.manifest_enabled === false) {
        manifestElement.removeAttribute('href');
      } else if (siteIcons.manifest_enabled === true) {
        const manifest = buildDynamicManifest(siteIcons, fallbackValues);

        const manifestBlob = new Blob([JSON.stringify(manifest)], {
          type: 'application/manifest+json',
        });

        manifestObjectURL = window.URL.createObjectURL(manifestBlob);

        manifestElement.setAttribute('href', manifestObjectURL);
      }
    }

    return () => {
      restoreFallbackMetadata();

      if (manifestObjectURL) {
        window.URL.revokeObjectURL(manifestObjectURL);
      }
    };
  }, [siteIcons]);

  useEffect(() => {
    activeLegalDocumentTypeRef.current = activeLegalDocumentType;
  }, [activeLegalDocumentType]);

  useEffect(() => {
    const synchronizeLegalDocumentWithHash = () => {
      const currentHash = getCurrentHash();

      const nextLegalDocumentType = getLegalDocumentTypeFromHash(currentHash);

      if (nextLegalDocumentType) {
        if (!activeLegalDocumentTypeRef.current) {
          const focusedElement = window.document.activeElement;

          setReturnFocusElement(focusedElement instanceof HTMLElement ? focusedElement : null);
        }
      } else {
        lastNonLegalHashRef.current = currentHash || DEFAULT_RETURN_HASH;
      }

      activeLegalDocumentTypeRef.current = nextLegalDocumentType;

      setActiveLegalDocumentType(nextLegalDocumentType);
    };

    window.addEventListener('hashchange', synchronizeLegalDocumentWithHash);

    window.addEventListener('popstate', synchronizeLegalDocumentWithHash);

    synchronizeLegalDocumentWithHash();

    return () => {
      window.removeEventListener('hashchange', synchronizeLegalDocumentWithHash);

      window.removeEventListener('popstate', synchronizeLegalDocumentWithHash);
    };
  }, []);

  const handleCloseLegalDocument = useCallback(() => {
    const currentHash = getCurrentHash();

    if (getLegalDocumentTypeFromHash(currentHash)) {
      const returnHash = lastNonLegalHashRef.current || DEFAULT_RETURN_HASH;

      window.history.pushState(
        {
          legalDocumentClosed: true,
        },
        '',
        returnHash,
      );
    }

    activeLegalDocumentTypeRef.current = null;

    setActiveLegalDocumentType(null);
  }, []);

  const handleManageCookies = useCallback(() => {
    setPreferencesRequest((currentRequest) => currentRequest + 1);
  }, []);

  const legalSettings = siteSettings?.legal;

  const legalDocuments = Array.isArray(legalSettings?.documents) ? legalSettings.documents : [];

  const activeLegalDocument = getLegalDocument(legalDocuments, activeLegalDocumentType);

  const closeLabel =
    typeof legalSettings?.close_label === 'string' && legalSettings.close_label.trim()
      ? legalSettings.close_label.trim()
      : 'Chiudi';

  return (
    <>
      <AppPage>
        <Hero topSettings={siteSettings?.top} headerSettings={siteSettings?.header} />

        <MenuSection menuSettings={siteSettings?.menu} />

        <Footer
          footerSettings={siteSettings?.footer}
          legalSettings={legalSettings}
          onManageCookies={handleManageCookies}
        />
      </AppPage>

      {isSiteSettingsResolved && (
        <CookieConsent legalSettings={legalSettings} preferencesRequest={preferencesRequest} />
      )}

      <LegalDocumentModal
        isOpen={Boolean(activeLegalDocumentType)}
        documentType={activeLegalDocumentType || 'privacy'}
        documentData={activeLegalDocument}
        closeLabel={closeLabel}
        onClose={handleCloseLegalDocument}
        returnFocusElement={returnFocusElement}
      />
    </>
  );
}

export default App;
